import { Component, ElementRef, EventEmitter, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { humanizeBytes, UploaderOptions, UploadFile, UploadInput, UploadOutput, UploadStatus } from 'ngx-uploader';
import { forkJoin } from 'rxjs';
import { AuthService } from '../../../../shared/services/authentication/auth.service';
import { PCodeServices } from '../../../../shared/services/pcode.service';
import { FilesService } from '../../services/pcode/files/files.service';
import { FILE_TYPES, UploaderDialogInput } from './upload.models';

@Component({
    selector: 'pc-uploader',
    templateUrl: './upload.component.html',
    styleUrls: ["./upload.component.scss"],
    standalone: false
})
export class UploadDialogComponent implements OnInit
{
	@ViewChild('uploader') uploaderInput: ElementRef;

	formData: FormData;
	files: UploadFile[];
	uploadInput: EventEmitter<UploadInput>;
	humanizeBytes: Function;
	dragOver: boolean;
	options: UploaderOptions;
	uploading = false;

	_filetypes;

	// upload form
	items: UntypedFormArray;
	public _form: UntypedFormGroup = this.fb.group(
		{
			items: this.fb.array([])
		}
	);

	// adding custom info
	createItem(file: UploadFile, fileName: string, fileTypeId: string): UntypedFormGroup
	{
		return this.fb.group({
			file: [file, [Validators.required]],
			fileName: [fileName, [Validators.required]],
			fileTypeId: fileTypeId
		});
	}

	// add file item
	addItem(file: UploadFile, fileName: string, fileTypeId: string): void
	{
		this.items = this._form.get('items') as UntypedFormArray;

		let _fileName = fileName;
		if (_fileName.indexOf('.') >= 0)
			_fileName = fileName.split('.')[0];

		this.items.push(this.createItem(file, _fileName, fileTypeId));
	}

	// constructor
	constructor(
		private dialogRef: MatDialogRef<UploadDialogComponent>,
		@Inject(MAT_DIALOG_DATA) private model: UploaderDialogInput,
		private pcodeService: PCodeServices,
		private translateService: TranslateService,
		private filesService: FilesService,
		private fb: UntypedFormBuilder
	)
	{
	}

	// on init
	ngOnInit()
	{
		this.options =
		{
			concurrency: 5,
			maxUploads: 9999,
			allowedContentTypes: this.model.allowedContentTypes
		};
		this.files = [];
		this.uploadInput = new EventEmitter<UploadInput>();
		this.humanizeBytes = humanizeBytes;

		const _fileTypesFilter = this.model.data.fileTypesFilter || null;
		const _fileTypesObs = this.filesService.getFileTypes(_fileTypesFilter);

		forkJoin([_fileTypesObs]).subscribe(results =>
		{
			this._filetypes = results[0] as [];
		});
	}

	// on upload output
	onUploadOutput(output: UploadOutput): void
	{
		//if (output.type === 'start' && typeof output.file !== 'undefined')
		//{
		//}

		if (output.type === 'allAddedToQueue')
		{
			//this.startUpload();
		}

		else if (output.type === 'addedToQueue' && typeof output.file !== 'undefined')
		{
			this.files.push(output.file);

			//const index = this.files.findIndex(file => typeof output.file !== 'undefined' && file.id === output.file.id);

			this.addItem(output.file, output.file.name, FILE_TYPES.GENERIC);
		}

		else if (output.type === 'uploading' && typeof output.file !== 'undefined')
		{
			this.uploading = true;

			const index = this.files.findIndex(file => typeof output.file !== 'undefined' && file.id === output.file.id);

			this.files[index] = output.file;
		}
		else if (output.type === 'cancelled' || output.type === 'removed')
		{
			this.files = this.files.filter((file: UploadFile) => file !== output.file);
		}
		else if (output.type === 'dragOver')
		{
			this.dragOver = true;
		}
		else if (output.type === 'dragOut')
		{
			this.dragOver = false;
		}
		else if (output.type === 'drop')
		{
			this.dragOver = false;
		}
		else if (output.type === 'rejected' && typeof output.file !== 'undefined')
		{
			this.pcodeService.notify('error', output.file.name + ':' + this.translateService.instant('Shared.Upload.Notifications.Rejected'))
			console.log(output.file.name + ' rejected');
		}

		this.files = this.files.filter(file => file.progress.status !== UploadStatus.Done);

		if (this.files.length === 0)
		{
			this.uploading = false;

			if (output.type === 'done')
			{
				this.pcodeService.notify('success', this.translateService.instant('Shared.Upload.Notifications.Success'));

				// closing dialog with no data
				this.dialogRef.close({ uploadComplete: true });

				this.files = [];

				this.items = this._form.get('items') as UntypedFormArray;
				while (this.items.length !== 0)
					this.items.removeAt(0)

				this.uploaderInput.nativeElement.value = '';
			}
		}
	}

	// start upload files
	startUpload(): void
	{
		const _filesInfos = this._form.controls.items['controls'] as UntypedFormGroup[];

		this.files.forEach((_file, index) =>
		{
			const _fileInfo = _filesInfos[index];
			const _uploadBaseData =
			{
				FileName: _fileInfo.value['fileName'],
				FileTypeId: _fileInfo.value['fileTypeId']
			};

			const event: UploadInput = {
				type: 'uploadFile',
				url: this.model.url,
				method: 'POST',
				headers: { 'Authorization': 'Bearer ' + AuthService.getAccessToken() },
				data: { ..._uploadBaseData, ...this.model.data },
				file: _file
			};

			this.uploadInput.emit(event);
		});
	}

	// cancel current upload
	cancelUpload(id: string): void
	{
		this.uploadInput.emit({ type: 'cancel', id: id });
	}

	// remove single file
	removeFile(id: string): void
	{
		this.uploadInput.emit({ type: 'remove', id: id });
	}

	// remove all files
	removeAllFiles(): void
	{
		this.uploadInput.emit({ type: 'removeAll' });
	}
}